// src/admin/pages/PlanRestrictionsPage.js
import React, { useState, useEffect } from 'react';
import { 
  Save, 
  AlertCircle, 
  CheckCircle,
  Building2,
  FolderOpen,
  HelpCircle,
  TrendingUp,
  BarChart3,
  Loader2,
  Users,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../config';

const PlanRestrictionsPage = () => {
  const [plans, setPlans] = useState([]);
  const [restrictions, setRestrictions] = useState({});
  const [planPricing, setPlanPricing] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null); // Track which plan is syncing
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const products = [
    { id: 'equity', name: 'Equity', icon: Building2, description: 'Company and shareholder management' },
    { id: 'dataroom', name: 'Dataroom', icon: FolderOpen, description: 'Document management and sharing' },
    { id: 'questions', name: 'Questions', icon: HelpCircle, description: 'Q&A and document requests' },
    { id: 'capraise', name: 'CapRaise', icon: TrendingUp, description: 'Capital raising and deals' },
    { id: 'investor_crm', name: 'Investor CRM', icon: Users, description: 'Investor contact management' },
    { id: 'marketData', name: 'MarketData', icon: BarChart3, description: 'Financial data and analytics' }
  ];

  useEffect(() => {
    fetchPlansAndRestrictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlansAndRestrictions = async () => {
    try {
      setLoading(true);
      
      // Fetch all plans
      const { data: plansData, error: plansError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (plansError) throw plansError;

      setPlans(plansData || []);

      // Initialize restrictions and pricing from plans
      const initialRestrictions = {};
      const initialPricing = {};
      (plansData || []).forEach(plan => {
        const planRestrictions = plan.product_restrictions || {};
        initialRestrictions[plan.plan_type] = {
          equity: planRestrictions.equity || getDefaultRestrictions(plan.plan_type, 'equity'),
          dataroom: planRestrictions.dataroom || getDefaultRestrictions(plan.plan_type, 'dataroom'),
          questions: planRestrictions.questions || getDefaultRestrictions(plan.plan_type, 'questions'),
          capraise: planRestrictions.capraise || getDefaultRestrictions(plan.plan_type, 'capraise'),
          investor_crm: planRestrictions.investor_crm || getDefaultRestrictions(plan.plan_type, 'investor_crm'),
          marketData: planRestrictions.marketData || getDefaultRestrictions(plan.plan_type, 'marketData')
        };
        
        // Store pricing (read-only reference for display)
        initialPricing[plan.plan_type] = {
          price_monthly: plan.price_monthly || 0,
          price_yearly: plan.price_yearly || 0
        };
      });

      setRestrictions(initialRestrictions);
      setPlanPricing(initialPricing);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans and restrictions');
    } finally {
      setLoading(false);
    }
  };

  const syncUsersOnPlan = async (plan) => {
    try {
      setSyncing(plan.plan_type);
      setError('');
      setMessage('');

      // Get all users on this plan
      const { data: subscriptions, error: subError } = await supabase
        .from('subscriptions')
        .select('user_id')
        .eq('plan_type', plan.plan_type)
        .in('status', ['active', 'trial']);

      if (subError) throw subError;

      if (!subscriptions || subscriptions.length === 0) {
        setMessage(`No active users found on ${plan.name} plan.`);
        setTimeout(() => setMessage(''), 3000);
        return;
      }

      // Get the current restrictions for this plan
      const planRestrictions = restrictions[plan.plan_type] || {};
      
      // Determine which products are enabled
      const enabledProducts = Object.entries(planRestrictions)
        .filter(([_, config]) => config?.enabled)
        .map(([productId]) => {
          // Map product IDs to database product names
          const productMap = {
            'equity': 'equity_manager',
            'dataroom': 'dataroom',
            'capraise': 'capraise'
          };
          return productMap[productId];
        })
        .filter(Boolean);

      let synced = 0;
      let errors = 0;

      // For each user, sync their product roles
      for (const sub of subscriptions) {
        try {
          // Remove existing creator roles for this user (except viewer roles from invites)
          const { error: deleteError } = await supabase
            .from('user_product_roles')
            .delete()
            .eq('user_id', sub.user_id)
            .eq('role', 'creator');

          if (deleteError) {
            console.error('Error removing old roles:', deleteError);
          }

          // Add roles for enabled products
          for (const product of enabledProducts) {
            const { error: insertError } = await supabase
              .from('user_product_roles')
              .upsert({
                user_id: sub.user_id,
                product_name: product,
                role: 'creator',
                updated_at: new Date().toISOString()
              }, { onConflict: 'user_id, product_name' });

            if (insertError) {
              console.error('Error adding role:', insertError);
              errors++;
            }
          }
          synced++;
        } catch (err) {
          console.error('Error syncing user:', err);
          errors++;
        }
      }

      if (errors > 0) {
        setMessage(`Synced ${synced} users on ${plan.name} plan with ${errors} errors.`);
      } else {
        setMessage(`Successfully synced ${synced} users on ${plan.name} plan.`);
      }
      setTimeout(() => setMessage(''), 5000);

    } catch (err) {
      console.error('Error syncing users:', err);
      setError(`Failed to sync users on ${plan.name} plan: ${err.message}`);
    } finally {
      setSyncing(null);
    }
  };

  const getDefaultRestrictions = (planType, productName) => {
    const defaults = {
      free: {
        equity: { max_companies: 1, max_shareholders: 5, enabled: true },
        dataroom: { max_projects: 3, max_items_per_project: 50, max_data_gb: 1, enabled: true },
        questions: { max_questions: 10, enabled: true },
        capraise: { max_deals: 0, enabled: false },
        investor_crm: { max_contacts: 0, enabled: false },
        marketData: { data_access: 'limited', enabled: true }
      },
      basic: {
        equity: { max_companies: 2, max_shareholders: 20, enabled: true },
        dataroom: { max_projects: 10, max_items_per_project: 200, max_data_gb: 10, enabled: true },
        questions: { max_questions: 50, enabled: true },
        capraise: { max_deals: 3, enabled: true },
        investor_crm: { max_contacts: 50, enabled: true },
        marketData: { data_access: 'standard', enabled: true }
      },
      pro: {
        equity: { max_companies: 5, max_shareholders: 50, enabled: true },
        dataroom: { max_projects: 20, max_items_per_project: -1, max_data_gb: -1, enabled: true },
        questions: { max_questions: -1, enabled: true },
        capraise: { max_deals: 5, enabled: true },
        investor_crm: { max_contacts: -1, enabled: true },
        marketData: { data_access: 'extended', enabled: true }
      }
    };

    return defaults[planType]?.[productName] || { enabled: false };
  };

  const handleRestrictionChange = (planType, productId, field, value) => {
    setRestrictions(prev => ({
      ...prev,
      [planType]: {
        ...prev[planType],
        [productId]: {
          ...prev[planType]?.[productId],
          [field]: field === 'enabled' ? value : (field.includes('max_') ? parseInt(value) || 0 : value)
        }
      }
    }));
  };

  // Pricing is read-only on this page - managed on Plans page

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');

      // Update each plan with its restrictions and pricing
      const updatePromises = plans.map(async (plan) => {
        const planRestrictions = restrictions[plan.plan_type] || {};
        const pricing = planPricing[plan.plan_type] || {};
        
        // Only update fields that are being changed, preserve all other fields
        const updateData = {
          product_restrictions: planRestrictions,
          updated_at: new Date().toISOString()
        };

        // Only include pricing fields if they're actually set
        if (pricing.price_monthly !== undefined) {
          updateData.price_monthly = pricing.price_monthly;
        } else if (plan.price_monthly !== undefined) {
          updateData.price_monthly = plan.price_monthly;
        }

        if (pricing.price_yearly !== undefined) {
          updateData.price_yearly = pricing.price_yearly;
        } else if (plan.price_yearly !== undefined) {
          updateData.price_yearly = plan.price_yearly;
        }

        if (pricing.trial_days !== undefined) {
          updateData.trial_days = pricing.trial_days;
        } else if (plan.trial_days !== undefined) {
          updateData.trial_days = plan.trial_days;
        }

        // Stripe IDs - only update if provided
        if (pricing.stripe_product_id !== undefined && pricing.stripe_product_id !== '') {
          updateData.stripe_product_id = pricing.stripe_product_id;
        }
        if (pricing.stripe_price_id_monthly !== undefined && pricing.stripe_price_id_monthly !== '') {
          updateData.stripe_price_id_monthly = pricing.stripe_price_id_monthly;
        }
        if (pricing.stripe_price_id_yearly !== undefined && pricing.stripe_price_id_yearly !== '') {
          updateData.stripe_price_id_yearly = pricing.stripe_price_id_yearly;
        }

        const { data, error } = await supabase
          .from('subscription_plans')
          .update(updateData)
          .eq('id', plan.id)
          .select();

        return { data, error };
      });

      const results = await Promise.all(updatePromises);
      const errors = results.filter(r => r.error);
      
      // Log detailed error information
      if (errors.length > 0) {
        console.error('Update errors:', errors);
        const errorMessages = errors.map(e => e.error?.message || 'Unknown error').join(', ');
        throw new Error(`Some updates failed: ${errorMessages}`);
      }

      if (errors.length > 0) {
        throw new Error('Some updates failed');
      }

      setMessage('Plans saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error saving plans:', err);
      setError('Failed to save plans. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getProductFields = (productId) => {
    const fields = {
      equity: [
        { key: 'max_companies', label: 'Max Companies', type: 'number' },
        { key: 'max_shareholders', label: 'Max Shareholders', type: 'number' }
      ],
      dataroom: [
        { key: 'max_projects', label: 'Max Projects', type: 'number' },
        { key: 'max_items_per_project', label: 'Max Items/Project', type: 'number', help: '-1 for unlimited' },
        { key: 'max_data_gb', label: 'Max Data GB', type: 'number', help: '-1 for unlimited' }
      ],
      questions: [
        { key: 'max_questions', label: 'Max Questions', type: 'number', help: '-1 for unlimited' }
      ],
      capraise: [
        { key: 'max_deals', label: 'Max Deals', type: 'number' }
      ],
      investor_crm: [
        { key: 'max_contacts', label: 'Max Contacts', type: 'number', help: '-1 for unlimited' }
      ],
      marketData: [
        { key: 'data_access', label: 'Data Access Level', type: 'select', options: ['limited', 'standard', 'extended'] }
      ]
    };
    return fields[productId] || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plan Restrictions</h1>
          <p className="text-gray-600 mt-2">Configure pricing and product restrictions for each subscription plan</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save All Changes
            </>
          )}
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
          <CheckCircle className="h-4 w-4 mr-2" />
          {message}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}

      {/* Pricing Section - Read Only Reference */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Pricing (Reference Only)</h2>
          <p className="text-xs text-gray-500 mt-1">Pricing is managed on the Plans page. This is shown for reference only.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 sticky left-0 bg-gray-50 z-10 min-w-[180px]">
                  Plan
                </th>
                {plans.map(plan => (
                  <th key={plan.id} className="px-4 py-3 text-center text-sm font-semibold text-gray-900 min-w-[200px]">
                    <div className="font-bold">{plan.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Monthly Price */}
              <tr>
                <td className="px-4 py-3 sticky left-0 bg-white z-10 font-medium text-sm text-gray-700">
                  Monthly Price (cents)
                </td>
                {plans.map(plan => {
                  return (
                    <td key={plan.id} className="px-4 py-3">
                      <div className="text-sm text-gray-700">
                        ${((plan.price_monthly || 0) / 100).toFixed(2)}
                      </div>
                    </td>
                  );
                })}
              </tr>
              {/* Yearly Price */}
              <tr>
                <td className="px-4 py-3 sticky left-0 bg-white z-10 font-medium text-sm text-gray-700">
                  Yearly Price (cents)
                </td>
                {plans.map(plan => {
                  return (
                    <td key={plan.id} className="px-4 py-3">
                      <div className="text-sm text-gray-700">
                        ${((plan.price_yearly || 0) / 100).toFixed(2)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Restrictions Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Product Restrictions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 sticky left-0 bg-gray-50 z-10 min-w-[180px]">
                  Product
                </th>
                {plans.map(plan => (
                  <th key={plan.id} className="px-4 py-3 text-center text-sm font-semibold text-gray-900 min-w-[200px]">
                    <div className="font-bold">{plan.name}</div>
                    <button
                      onClick={() => syncUsersOnPlan(plan)}
                      disabled={syncing === plan.plan_type}
                      className="mt-1 text-xs text-blue-600 hover:text-blue-800 flex items-center justify-center mx-auto disabled:opacity-50"
                      title={`Sync all users on ${plan.name} plan with current restrictions`}
                    >
                      {syncing === plan.plan_type ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Sync Users
                        </>
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map(product => {
                const fields = getProductFields(product.id);
                const IconComponent = product.icon;

                return (
                  <tr key={product.id} className="hover:bg-gray-50">
                    {/* Product Column */}
                    <td className="px-4 py-3 sticky left-0 bg-white z-10">
                      <div className="flex items-center">
                        <IconComponent className="h-4 w-4 text-gray-600 mr-2 flex-shrink-0" />
                        <div className="font-semibold text-gray-900 text-sm">{product.name}</div>
                      </div>
                    </td>

                    {/* Plan Columns */}
                    {plans.map(plan => {
                      const productRestrictions = restrictions[plan.plan_type]?.[product.id] || {};
                      
                      return (
                        <td key={plan.id} className="px-4 py-3 align-top">
                          <div className="space-y-3">
                            {/* Enabled Toggle */}
                            <div className="flex items-center justify-center">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={productRestrictions.enabled || false}
                                  onChange={(e) => handleRestrictionChange(plan.plan_type, product.id, 'enabled', e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                                <span className="ml-2 text-xs text-gray-700">
                                  {productRestrictions.enabled ? 'On' : 'Off'}
                                </span>
                              </label>
                            </div>

                            {/* Restriction Fields */}
                            {productRestrictions.enabled && (
                              <div className="space-y-2">
                                {fields.map(field => (
                                  <div key={field.key}>
                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                      {field.label}
                                    </label>
                                    {field.type === 'select' ? (
                                      <select
                                        value={productRestrictions[field.key] || ''}
                                        onChange={(e) => handleRestrictionChange(plan.plan_type, product.id, field.key, e.target.value)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      >
                                        {field.options.map(opt => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <input
                                        type="number"
                                        value={productRestrictions[field.key] || ''}
                                        onChange={(e) => handleRestrictionChange(plan.plan_type, product.id, field.key, e.target.value)}
                                        placeholder={field.help || '0'}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlanRestrictionsPage;

